/**
 * UI-only store for the in-house SupportChat panel's open state.
 *
 * SupportChat lives in AppLayout (rendered only for patients in the
 * 'inhouse-chat' experiment's variant-a). Lifting `open` out of the
 * component lets other surfaces — e.g. the "Chat with us" link in the
 * Need Help footers (Sidebar + OverflowDrawer) — open the panel without
 * prop-drilling. Mirrors `features/marketplace/stores/cartUi.ts`.
 */

import { useSyncExternalStore } from 'react';

let open = false;
const listeners = new Set<() => void>();

// Care-team messaging experiment: while the unified Messages drawer is
// mounted it takes over every support entry point (Launcher, "Chat with
// us", "Contact support" on orders, billing links) without each caller
// knowing about the experiment.
type SupportDelegate = (opts: { chooser: boolean }) => void;
let delegate: SupportDelegate | null = null;

function emit() {
  listeners.forEach((l) => l());
}

export const supportChat = {
  /** `chooser` — generic help links ask which team to reach instead of
   * going straight to Support (only meaningful under the delegate). */
  open(opts: { chooser?: boolean } = {}) {
    if (delegate) {
      delegate({ chooser: Boolean(opts.chooser) });
      return;
    }
    if (open) return;
    open = true;
    emit();
  },
  setDelegate(next: SupportDelegate | null) {
    delegate = next;
    if (next && open) {
      open = false;
      emit();
    }
  },
  close() {
    if (!open) return;
    open = false;
    emit();
  },
  toggle() {
    open = !open;
    emit();
  },
};

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useSupportChatOpen(): boolean {
  return useSyncExternalStore(subscribe, () => open);
}
